import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Flex, Form, Input, List, Radio, Tag, Typography, message } from 'antd';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../lib/api-client';
import { formatDateTime } from '../lib/format';
import { formatMoney, isPositive } from '../lib/money';
import { ActionDrawer } from './ActionDrawer';
import { PrintButton } from './print/PrintButton';
import {
  approveRefund,
  cancelRefund,
  invalidateAfterRefund,
  type PayoutMethod,
  type Refund,
} from './refund-api';

const { Text } = Typography;

const METHODS: PayoutMethod[] = ['CASH', 'CARD', 'BANK_TRANSFER'];
/** Ringgit and sen, as typed: never parsed to a number. */
const RINGGIT = /^\d+(\.\d{1,2})?$/;

/**
 * Approve a refund of a cancelled booking or a voided invoice. No money moves
 * here: it goes on the Till's Refunds to pay list and someone else pays it out.
 */
export function RefundDrawer({
  source,
  refundable,
  open,
  onClose,
  onDone,
}: {
  source: { bookingUid: string } | { invoiceUid: string };
  refundable: string;
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState(refundable);
  const [method, setMethod] = useState<PayoutMethod>('CASH');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) {
      setAmount(refundable);
      setMethod('CASH');
      setReason('');
    }
  }, [open, refundable]);

  const approve = useMutation({
    mutationFn: () => approveRefund({ ...source, amount: amount.trim(), method, reason: reason.trim() }),
    onSuccess: () => {
      invalidateAfterRefund(queryClient);
      void message.success(t('refunds.approved'));
      onDone();
      onClose();
    },
    onError: (e: unknown) => {
      void message.error(e instanceof ApiError ? e.message : t('refunds.approveFailed'));
    },
  });

  const valid = RINGGIT.test(amount.trim()) && isPositive(amount.trim());

  return (
    <ActionDrawer
      open={open}
      title={t('refunds.approve')}
      submitLabel={t('refunds.approve')}
      submitting={approve.isPending}
      submitDisabled={!valid || reason.trim().length < 3}
      onClose={onClose}
      onSubmit={() => {
        approve.mutate();
      }}
      consequence={t('refunds.approveConsequence', {
        amount: valid ? amount.trim() : '0.00',
        method: t(`refunds.method.${method}`),
      })}
    >
      <Form layout="vertical">
        <Form.Item
          label={t('refunds.fields.amount')}
          htmlFor="refund-amount"
          help={t('refunds.amountHelp', { amount: formatMoney(refundable) })}
        >
          <Input
            id="refund-amount"
            inputMode="decimal"
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
            }}
          />
        </Form.Item>
        <Form.Item label={t('refunds.fields.method')}>
          <Radio.Group
            value={method}
            onChange={(e) => {
              setMethod(e.target.value as PayoutMethod);
            }}
            optionType="button"
            buttonStyle="solid"
            options={METHODS.map((m) => ({ value: m, label: t(`refunds.method.${m}`) }))}
          />
        </Form.Item>
        <Form.Item label={t('refunds.fields.reason')} htmlFor="refund-reason" required>
          <Input.TextArea
            id="refund-reason"
            rows={3}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
            }}
            placeholder={t('refunds.reasonPlaceholder')}
          />
        </Form.Item>
      </Form>
    </ActionDrawer>
  );
}

/** Refunds against one source: waiting, paid and withdrawn, with Cancel where the server says the user may. */
export function RefundList({
  refunds,
  printPaid = false,
}: {
  refunds: Refund[];
  /** Shows a print icon on each refund already paid out. */
  printPaid?: boolean;
}): ReactNode {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [cancelling, setCancelling] = useState<Refund | null>(null);
  const [reason, setReason] = useState('');

  const withdraw = useMutation({
    mutationFn: (refund: Refund) => cancelRefund(refund.uid, reason.trim()),
    onSuccess: () => {
      invalidateAfterRefund(queryClient);
      setCancelling(null);
      void message.success(t('refunds.cancelled'));
    },
    onError: (e: unknown) => {
      void message.error(e instanceof ApiError ? e.message : t('refunds.cancelFailed'));
    },
  });

  if (refunds.length === 0) return null;

  return (
    <>
      <List
        size="small"
        dataSource={refunds}
        rowKey={(r) => r.uid}
        renderItem={(r) => (
          <List.Item>
            <Flex justify="space-between" align="flex-start" gap={12} style={{ width: '100%' }}>
              <Flex vertical gap={2}>
                <Flex gap={8} align="center" wrap>
                  <Text strong className="tabular">
                    {r.refundNo ?? t('refunds.waiting')}
                  </Text>
                  <Tag color={r.status === 'PAID' ? 'success' : r.status === 'CANCELLED' ? undefined : 'warning'}>
                    {t(`refunds.status.${r.status}`)}
                  </Tag>
                </Flex>
                <Text type="secondary">
                  {formatDateTime(r.approvedAt)} · {t(`till.method.${r.method}`, { defaultValue: r.method })}
                </Text>
              </Flex>
              <Flex vertical align="flex-end" gap={4}>
                <Flex align="center" gap={8}>
                  <Text strong>{formatMoney(r.amount)}</Text>
                  {printPaid && r.printable && (
                    <PrintButton doc="refund" uid={r.uid} label={t('print.printRefund')} size="small" iconOnly />
                  )}
                </Flex>
                {r.canCancel && (
                  <Button
                    size="small"
                    onClick={() => {
                      setReason('');
                      setCancelling(r);
                    }}
                  >
                    {t('refunds.cancel')}
                  </Button>
                )}
              </Flex>
            </Flex>
          </List.Item>
        )}
      />
      <ActionDrawer
        open={cancelling !== null}
        danger
        title={t('refunds.cancel')}
        submitLabel={t('refunds.cancel')}
        submitting={withdraw.isPending}
        submitDisabled={reason.trim().length < 3}
        onClose={() => {
          setCancelling(null);
        }}
        onSubmit={() => {
          if (cancelling !== null) withdraw.mutate(cancelling);
        }}
        consequence={t('refunds.cancelConsequence', {
          amount: cancelling?.amount ?? '0.00',
        })}
      >
        <Form layout="vertical">
          <Form.Item label={t('refunds.fields.reason')} htmlFor="refund-cancel-reason" required>
            <Input.TextArea
              id="refund-cancel-reason"
              rows={3}
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
              }}
            />
          </Form.Item>
        </Form>
      </ActionDrawer>
    </>
  );
}
